import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, PageHeader, Badge } from '../components/UI';
import { fmt } from '../utils/helpers';

// ── Chevron icon ──────────────────────────────────────────────────────────────
const IconChevron = ({ open }) => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Spinner ───────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ width:16, height:16, border:'2px solid var(--border2)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.6s linear infinite', flexShrink:0 }} />
);

// ── Single expandable plan row ────────────────────────────────────────────────
function PlanRow({ plan, index, maxRev }) {
  const [open, setOpen]           = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [filter, setFilter]       = useState('all');
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [fetched, setFetched]     = useState(false);
  const navigate = useNavigate();

  const pct = (parseFloat(plan.total_revenue || 0) / maxRev) * 100;

  const fetchSubscribers = async (statusFilter) => {
    setLoadingSubs(true);
    try {
      const r = await api.getPlanSubscribers(plan.plan_id, statusFilter !== 'all' ? statusFilter : undefined);
      setSubscribers(r.subscribers || []);
      setFetched(true);
    } catch {
      setSubscribers([]);
    } finally {
      setLoadingSubs(false);
    }
  };

  const toggle = () => {
    if (!open && !fetched) fetchSubscribers(filter);
    setOpen(o => !o);
  };

  const changeFilter = (f) => {
    setFilter(f);
    setFetched(false);
    fetchSubscribers(f);
  };

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* ── Plan header row — clickable to expand ── */}
      <button
        onClick={toggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 18px', textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        {/* Bar + labels */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'Geist Mono,monospace', fontSize: 12, background: 'var(--surface2)', padding: '2px 8px', borderRadius: 5, color: 'var(--accent)' }}>
                {plan.plan_name}
              </code>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Badge color="green">{plan.active_count} active</Badge>
                <Badge color="gray">{plan.expired_count} expired</Badge>
                {plan.cancelled_count > 0 && <Badge color="red">{plan.cancelled_count} cancelled</Badge>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'Geist Mono,monospace', color: 'var(--green)' }}>{fmt.naira(plan.total_revenue)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{plan.count} sold</div>
            </div>
          </div>
          <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: `hsl(${200 + index * 30},75%,58%)`, borderRadius: 99, transition: 'width 0.8s ease' }} />
          </div>
        </div>

        {/* Chevron */}
        <div style={{ color: 'var(--text3)' }}>
          <IconChevron open={open} />
        </div>
      </button>

      {/* ── Expanded subscriber list ── */}
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)', padding: '0 18px 14px' }}>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 6, padding: '12px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>Filter:</span>
            {['all', 'active', 'expired', 'cancelled'].map(f => (
              <button key={f} onClick={() => changeFilter(f)}
                style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', border: '1px solid',
                  borderColor: filter === f ? 'var(--accent)' : 'var(--border2)',
                  background: filter === f ? 'var(--accent-dim)' : 'transparent',
                  color: filter === f ? 'var(--accent)' : 'var(--text3)',
                  textTransform: 'capitalize',
                }}>
                {f}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loadingSubs && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0', color: 'var(--text3)', fontSize: 13 }}>
              <Spinner /> Loading subscribers…
            </div>
          )}

          {/* Empty */}
          {!loadingSubs && subscribers.length === 0 && (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No subscribers{filter !== 'all' ? ` with status "${filter}"` : ''}.
            </div>
          )}

          {/* Subscriber rows */}
          {!loadingSubs && subscribers.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {subscribers.map(s => {
                const u = s.user;
                if (!u) return null;
                return (
                  <button
                    key={s.id}
                    onClick={() => navigate(`/users/${u.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, padding: '10px 12px', borderRadius: 8, border: 'none',
                      background: 'none', cursor: 'pointer', textAlign: 'left',
                      fontFamily: 'inherit', transition: 'background 0.1s',
                      flexWrap: 'wrap',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    {/* Avatar + name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-dim)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, color: 'var(--accent)', flexShrink: 0,
                      }}>
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                      </div>
                    </div>

                    {/* Meta */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, fontFamily: 'Geist Mono,monospace', fontWeight: 600, color: 'var(--green)' }}>
                          {fmt.naira(u.wallet?.balance)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>balance</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, fontFamily: 'Geist Mono,monospace', color: 'var(--text2)' }}>
                          {s.expires_at ? fmt.date(s.expires_at) : '—'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>expires</div>
                      </div>
                      <div style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                        background: s.status === 'active' ? 'rgba(34,197,94,0.12)'
                          : s.status === 'cancelled' ? 'rgba(239,68,68,0.1)'
                          : 'rgba(100,100,100,0.1)',
                        color: s.status === 'active' ? '#22c55e'
                          : s.status === 'cancelled' ? 'var(--red)'
                          : 'var(--text3)',
                        textTransform: 'capitalize',
                      }}>
                        {s.status}
                      </div>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" style={{ color: 'var(--text3)', flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Subscriptions() {
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading]     = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.getPlanBreakdown()
      .then(r => setBreakdown(r.breakdown || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  const totalRevenue = breakdown.reduce((s, p) => s + parseFloat(p.total_revenue || 0), 0);
  const totalSold    = breakdown.reduce((s, p) => s + parseInt(p.count || 0), 0);
  const totalActive  = breakdown.reduce((s, p) => s + parseInt(p.active_count || 0), 0);
  const maxRev       = breakdown.reduce((m, p) => Math.max(m, parseFloat(p.total_revenue || 0)), 1);

  return (
    <div className="page-enter">
      <PageHeader title="Subscriptions" subtitle="Plan performance and revenue breakdown" />

      {/* ── Summary tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Revenue', value: fmt.naira(totalRevenue), color: 'var(--green)'  },
          { label: 'Total Sold',    value: totalSold.toLocaleString(),   color: 'var(--accent)' },
          { label: 'Active Now',    value: totalActive.toLocaleString(), color: 'var(--purple)' },
        ].map(s => (
          <Card key={s.label} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne,sans-serif', color: s.color, wordBreak: 'break-all' }}>{s.value}</div>
          </Card>
        ))}
      </div>

      {/* ── Expandable plan list ── */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Plans & Subscribers</h3>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>Tap a plan to see its subscribers</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>
        ) : breakdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 13 }}>No subscription data.</div>
        ) : (
          breakdown.map((plan, i) => (
            <PlanRow key={plan.plan_id || i} plan={plan} index={i} maxRev={maxRev} />
          ))
        )}
      </Card>
    </div>
  );
}